import { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import FormattedText from '@/components/shared/FormattedText';
import { useClampOverflow } from '@/hooks/useClampOverflow';

/**
 * 文字 line-clamp 截斷 + see more / see less 切換的共用元件。
 *
 * clamp 由 CSS class 控制：收合時套 clampClassName（如 post-content-container / comment-container），
 * 展開時移除。是否溢出由 useClampOverflow 偵測，溢出才顯示切換鈕。
 *
 * @param {Object}   props
 * @param {string}   props.text           內容文字（以 FormattedText 渲染）
 * @param {string}   props.clampClassName 收合時套用的 line-clamp class
 * @param {string}   [props.className]    容器固定 class
 * @param {boolean}  [props.forceFull]    強制完整顯示、不顯示切換鈕（如詳情頁）
 * @param {React.ReactNode} [props.prefix] 內容前綴（如打賞 badge），與文字同在 clamp 容器內
 * @param {(state: {expanded: boolean, overflowing: boolean}) => React.ReactNode} [props.beforeToggle]
 *                                         clamp 容器之後、切換鈕之前的附加內容（如貼文 tags），
 *                                         依展開/溢出狀態決定顯示。切換鈕須永遠是這一區的最後一個元素，
 *                                         否則展開後會夾在內文與附加內容中間
 */
const ClampText = ({ text, clampClassName, className = '', forceFull = false, prefix = null, beforeToggle }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  // 內容被編輯時元件不會 remount（列表 key 不變），展開狀態必須跟著新內容重置：
  // 新內容的截斷情況與舊內容無關，沿用舊狀態會讓切換鈕與實際內容對不上
  const [renderedText, setRenderedText] = useState(text);
  if (text !== renderedText) {
    setRenderedText(text);
    setExpanded(false);
  }

  const showFull = forceFull || expanded;
  const [ref, overflowing] = useClampOverflow(showFull, text);
  const showToggle = overflowing && !forceFull;

  return (
    <>
      <div ref={ref} className={`${className} ${showFull ? '' : clampClassName}`.trim()}>
        {prefix}
        <FormattedText text={text} />
      </div>

      {beforeToggle && beforeToggle({ expanded: showFull, overflowing })}

      {showToggle && (
        <div className="mt-1">
          <button
            className="btn-link text-primary p-0 border-0 bg-transparent small see-more-btn"
            onClick={() => setExpanded(prev => !prev)}
            style={{ textDecoration: 'none' }}
          >
            {expanded ? t('see_less') : t('see_more')}
          </button>
        </div>
      )}
    </>
  );
};

ClampText.propTypes = {
  text: PropTypes.string.isRequired,
  clampClassName: PropTypes.string.isRequired,
  className: PropTypes.string,
  forceFull: PropTypes.bool,
  prefix: PropTypes.node,
  beforeToggle: PropTypes.func,
};

export default ClampText;
